import type { Route } from './routes.ts';

/**
 * The app's top-level destinations — §8's page table, as data.
 *
 * In core rather than in the shell for the reason R13 exists: a TV needs the same four destinations,
 * in the same order, with the same sign-in gate, and a `<nav>` written in React would have to be
 * re-derived from prose in Compose. What the shell adds is the two things that *are* per-platform: a
 * label (R7 — core ships no display strings) and an icon.
 *
 * **Order is the D-pad order.** On the web it is also the Tab order, which is why it is a list and
 * not a record: a `Record` has no order anyone can rely on, and "the nav moved" is exactly the kind
 * of change that should show up in a diff here.
 */

export type NavDestinationId = 'popular' | 'subscriptions' | 'playlists' | 'history';

export interface NavDestination {
  readonly id: NavDestinationId;
  readonly route: Route;
  /**
   * §8's signed-out behaviour and §13.2: these are **disabled with a sign-in affordance**, never
   * hidden and never shown broken. It is a property of the destination rather than of the screen, so
   * that the shell can render the gate before the page it gates exists — the alternative is five
   * screens each discovering the rule for themselves.
   */
  readonly requiresAuth: boolean;
}

export const NAV_DESTINATIONS: readonly NavDestination[] = [
  // Popular rather than Home: `/` is nobody's destination now — it redirects to the landing
  // setting's page — and the brand link is what still points at it.
  { id: 'popular', route: { kind: 'feed', feed: 'popular' }, requiresAuth: false },
  // Trending was the second entry until the screen was retired: its rows are a section *on* the
  // popular page now, behind a setting, so there is nothing left for the nav to point at. `/feed/trending`
  // still parses — as the popular feed (`FEED_KINDS`) — which is why no destination has to describe it.
  { id: 'subscriptions', route: { kind: 'feed', feed: 'subscriptions' }, requiresAuth: true },
  { id: 'playlists', route: { kind: 'feed', feed: 'playlists' }, requiresAuth: true },
  { id: 'history', route: { kind: 'feed', feed: 'history' }, requiresAuth: true },
  // The queue is deliberately *not* here, and there is nothing for it to point at: a queue has no
  // route of its own. It is shown in the watch page's side panel, which the page draws exactly when
  // there is a queue to show ([queue.md](../../../../docs/specs/queue.md)).
];

/**
 * Which destination the current route belongs to, or `null` when the reader is somewhere the nav
 * does not describe — a video, a channel, a search, a 404.
 *
 * `null` is a real answer, not a fallback to Home: marking Home as current while a video is playing
 * tells a screen-reader user they are on the home page.
 */
export function activeNavId(route: Route): NavDestinationId | null {
  // A single playlist belongs to the Playlists section: it is reached from that list and
  // Back returns there, so the nav keeps the destination lit the way the manage page keeps
  // Subscriptions lit. `/` marks nothing — it redirects to the landing destination immediately,
  // and that destination then marks itself.
  if (route.kind === 'playlist') return 'playlists';
  if (route.kind !== 'feed') return null;

  // The manage-subscriptions page is the Subscriptions section's own sub-page — it is reached from
  // there and returns there — so the nav keeps that destination lit rather than going dark.
  if (route.feed === 'channels') return 'subscriptions';

  for (const destination of NAV_DESTINATIONS) {
    if (destination.route.kind === 'feed' && destination.route.feed === route.feed) return destination.id;
  }
  return null;
}
